import {
    CalendarDate,
    CalendarDateTime,
    parseAbsolute,
    toCalendarDate,
    toCalendarDateTime,
} from "@internationalized/date";

export function isoToCalendarDateInTz(iso: string, tz: string): CalendarDate | null {
    if (!iso?.trim()) {
return null;
}
    try {
        return toCalendarDate(parseAbsolute(iso, tz));
    } catch {
        return null;
    }
}

export function isoToZonedDateTime(iso: string, tz: string): CalendarDateTime | null {
    if (!iso?.trim()) {
return null;
}
    try {
        return toCalendarDateTime(parseAbsolute(iso, tz));
    } catch {
        return null;
    }
}

/** Wall-clock minutes since local midnight in the given IANA timezone. */
export function minutesSinceMidnightInTz(iso: string, tz: string): number | null {
    const dt = isoToZonedDateTime(iso, tz);
    if (!dt) {
return null;
}
    return dt.hour * 60 + dt.minute;
}

export function minutesSinceMidnightFromParts(hour: number, minute: number): number {
    return hour * 60 + minute;
}

export function formatInstantInTz(
    iso: string | Date,
    tz: string,
    options: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    },
): string {
    const d = iso instanceof Date ? iso : new Date(iso);
    if (Number.isNaN(d.getTime())) {
return "—";
}
    return new Intl.DateTimeFormat("pt-BR", { ...options, timeZone: tz }).format(d);
}

function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

/** ISO-8601 offset for an instant in the given IANA zone (e.g. `-03:00`, `+02:00`). */
export function formatTimezoneOffsetForInstant(instant: Date, tz: string): string {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "longOffset",
    }).formatToParts(instant);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
    const match = raw.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!match) {
return "+00:00";
}
    const sign = match[1];
    const hours = pad2(Number(match[2]));
    const minutes = pad2(Number(match[3] ?? 0));
    return `${sign}${hours}:${minutes}`;
}

/**
 * Serializes a wall-clock date/time in the profile timezone with explicit offset.
 * Avoids UTC (`Z`) strings that look 2–3h earlier than the time shown in the picker.
 */
export function calendarDateTimeToInstantIso(dt: CalendarDateTime, tz: string): string {
    const wall = `${dt.year}-${pad2(dt.month)}-${pad2(dt.day)}T${pad2(dt.hour)}:${pad2(dt.minute)}:${pad2(dt.second)}`;
    const offset = formatTimezoneOffsetForInstant(dt.toDate(tz), tz);
    return `${wall}${offset}`;
}

/** UTC epoch ms for a wall-clock slot on a calendar day in the given timezone. */
export function slotStartMsFromPartsInTz(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    tz: string,
): number {
    const cd = new CalendarDateTime(year, month, day, hour, minute, 0);
    return cd.toDate(tz).getTime();
}

export function isSameCalendarDayInTz(
    isoA: string,
    year: number,
    month: number,
    day: number,
    tz: string,
): boolean {
    const cd = isoToCalendarDateInTz(isoA, tz);
    if (!cd) {
return false;
}
    return cd.year === year && cd.month === month && cd.day === day;
}

/**
 * True when the scheduled start calendar day is strictly before today in `tz`.
 * Compares calendar dates only — not `scheduledAt < now` or end-time rules.
 */
export function isPastCalendarDayInTz(
    scheduledAt: string,
    tz: string,
    now: Date | string = new Date(),
): boolean {
    const startDay = isoToCalendarDateInTz(scheduledAt, tz);
    if (!startDay) {
return false;
}
    const nowIso = typeof now === "string" ? now : now.toISOString();
    const today = isoToCalendarDateInTz(nowIso, tz);
    if (!today) {
return false;
}
    return startDay.compare(today) < 0;
}
